/**
 * Pure, AWS-free, DOM-free helpers for the container-listing browse/print UI
 * (Storage Container Listing feature).
 *
 * The Storage page's browse section keeps its non-trivial logic — page
 * accumulation/de-duplication, the label-request reference for a listed entry,
 * the multi-label sheet partition, and selection toggling — here as pure
 * exported functions so they can be unit- and property-tested without a DOM
 * testing library (the frontend jest env is `node`). The React components
 * import these helpers and render the plain data structures they return.
 *
 * Nothing in this file imports React, a browser API, or an AWS SDK. The wire
 * shapes (`ContainerListing`, `ContainerListingEntry`, `ListableContainerType`)
 * come from `@tcvs/shared` (via `../shared/types`) and are never redeclared.
 *
 * Validates: Requirements 6.3, 7.2, 8.1, 8.3, 8.5, 10.1
 */

import type { ApiResult } from '../auth/api-client';
import type {
  ContainerListing,
  ContainerListingEntry,
  ListableContainerType,
  QrToken,
} from '../shared/types';
import type { LabelResponse } from './storage-api';

// ---------------------------------------------------------------------------
// Page accumulation
// ---------------------------------------------------------------------------

/**
 * Accumulate a freshly-fetched listing page onto the entries already shown,
 * preserving order and dropping duplicates (Req 6.3).
 *
 * The listing route returns a single deterministic ordering whose successive
 * pages concatenate to each matching entity exactly once, so under normal
 * operation `page.items` introduces no duplicates. This helper is nonetheless
 * defensively de-duplicating on `entityId`: a "Load more" that re-fetches the
 * same cursor (e.g. a double click) must never show a row twice. Existing
 * entries win — the first occurrence of an `entityId` is kept and its position
 * preserved — so the accumulated list stays globally ordered.
 */
export function appendPage(
  acc: readonly ContainerListingEntry[],
  page: ContainerListing,
): ContainerListingEntry[] {
  const seen = new Set<string>(acc.map((entry) => entry.entityId));
  const result: ContainerListingEntry[] = [...acc];
  for (const entry of page.items) {
    if (seen.has(entry.entityId)) continue;
    seen.add(entry.entityId);
    result.push(entry);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Create-section recent entries
// ---------------------------------------------------------------------------

const MAX_RECENT_CREATED_ENTRIES = 10;

/** Prepend one creation while retaining only the current session's newest ten. */
export function prependCreatedEntry<T>(entry: T, entries: readonly T[]): T[] {
  return [entry, ...entries].slice(0, MAX_RECENT_CREATED_ENTRIES);
}

/**
 * Select the recent creations that belong in one Create Container section.
 *
 * The Create page keeps one bounded, newest-first session list so creation
 * order remains global. The UI renders filtered views below the corresponding
 * forms; filtering deliberately preserves that order and object identity.
 */
export function createdEntriesForType<
  T extends { entity: Pick<ContainerListingEntry, 'entityType'> },
>(entries: readonly T[], entityType: ContainerListingEntry['entityType']): T[] {
  return entries.filter((entry) => entry.entity.entityType === entityType);
}

// ---------------------------------------------------------------------------
// Label request reference
// ---------------------------------------------------------------------------

/** Map an entity's `entityType` to the plural `{type}` path segment. */
const TYPE_SEGMENT: Record<ContainerListingEntry['entityType'], ListableContainerType> = {
  RACK: 'racks',
  SHELF: 'shelves',
  BOX: 'boxes',
  ROW: 'rows',
  BATCH: 'batches',
};

/**
 * Derive the `(type, id)` pair sufficient to call
 * `GET /storage/{type}/{id}/labels` for a listed entry (Req 7.2, 10.1).
 *
 * Every listed entry carries `entityId` + `entityType`, which is exactly what
 * the existing label route needs, so no extra lookup is required.
 *
 * The returned `id` is the **bare opaque id body** (the ULID), NOT the full
 * `tcvs:<type>:<id>` token. The label route's `{id}` is a single path segment,
 * and a full token's colons survive `encodeURIComponent` as `%3A` that API
 * Gateway REST does not decode — so passing the token would arrive as
 * `tcvs%3Ashelf%3A...`, fail the handler's `tcvs:` prefix check, and be wrapped
 * a second time into `tcvs:shelf:tcvs%3Ashelf%3A...` (a not-found id). Sending
 * only the ULID body keeps the segment colon-free; the handler reconstructs the
 * full id from `{type}` + `{id}`.
 */
export function labelRequestRef(entry: ContainerListingEntry): {
  type: ListableContainerType;
  id: string;
} {
  return { type: TYPE_SEGMENT[entry.entityType], id: idBody(entry.entityId) };
}

/**
 * Strip the `tcvs:<type>:` prefix from a Storage_Entity_Id, returning just the
 * opaque id body. Tolerates an id that is already bare (returns it unchanged).
 */
function idBody(entityId: string): string {
  const lastColon = entityId.lastIndexOf(':');
  return lastColon === -1 ? entityId : entityId.slice(lastColon + 1);
}

// ---------------------------------------------------------------------------
// Drill-down (Browse → contents)
// ---------------------------------------------------------------------------

/**
 * The `(type, id)` pair sufficient to call `GET /storage/{type}/{id}/contents`
 * for an entity addressed by its `entityType` + `entityId`. Same id rules as
 * {@link labelRequestRef}: the **bare ULID body** (colon-free path segment), not
 * the full `tcvs:<type>:<id>` token, which API Gateway would mangle.
 */
export function entityContentsRef(
  entityType: ContainerListingEntry['entityType'],
  entityId: string,
): { type: ListableContainerType; id: string } {
  return { type: TYPE_SEGMENT[entityType], id: idBody(entityId) };
}

/**
 * The react-router path to a Storage_Entity's detail page
 * (`/storage/{type}/{id}`), using the bare ULID body as the `{id}` segment —
 * the same id rule as {@link entityContentsRef} / {@link labelRequestRef}.
 */
export function entityDetailHref(
  entityType: ContainerListingEntry['entityType'],
  entityId: string,
): string {
  return `/storage/${TYPE_SEGMENT[entityType]}/${encodeURIComponent(idBody(entityId))}`;
}

/**
 * A single rendered child in the Browse drill-down, normalized from the
 * `kind`-discriminated `GET .../contents` payload so the UI renders one shape:
 *
 * - `container` — a Box or Storage_Batch the operator can drill into further
 *   (carries the `(type, id)` for the next contents request and a display label).
 * - `card` — a leaf Card placement that links to its catalog detail page.
 * - `unknown` — an item that matched neither shape; rendered as raw text so a
 *   schema surprise degrades gracefully instead of throwing.
 */
export type DrillChild =
  | {
      kind: 'container';
      entityType: ContainerListingEntry['entityType'];
      entityId: string;
      sourceRunId?: string;
    }
  | {
      kind: 'card';
      cardId: string;
      state?: string;
      thumbnailUrl?: string;
      backThumbnailUrl?: string;
      shortCode?: string;
      sku?: string;
      batchIndex?: number;
      currentBatchPosition?: number;
      currentBatchSize?: number;
    }
  | { kind: 'unknown'; text: string };

/**
 * Normalize one item from a contents page into a {@link DrillChild}. `kind` is
 * the contents response's discriminant (`boxes` / `batches` → containers,
 * `cards` → card leaves). Pure and defensive — an item missing the expected key
 * for its kind falls through to `unknown` rather than throwing.
 */
export function toDrillChild(
  kind: 'shelves' | 'boxes' | 'rows' | 'batches' | 'cards',
  item: unknown,
): DrillChild {
  if (typeof item !== 'object' || item === null) {
    return { kind: 'unknown', text: String(item) };
  }
  const rec = item as Record<string, unknown>;
  if (kind === 'cards') {
    if (typeof rec['cardId'] === 'string') {
      return {
        kind: 'card',
        cardId: rec['cardId'],
        ...(typeof rec['state'] === 'string' ? { state: rec['state'] } : {}),
        ...(typeof rec['thumbnailUrl'] === 'string' ? { thumbnailUrl: rec['thumbnailUrl'] } : {}),
        ...(typeof rec['backThumbnailUrl'] === 'string'
          ? { backThumbnailUrl: rec['backThumbnailUrl'] }
          : {}),
        ...(typeof rec['shortCode'] === 'string' ? { shortCode: rec['shortCode'] } : {}),
        ...(typeof rec['sku'] === 'string' ? { sku: rec['sku'] } : {}),
        ...(typeof rec['batchIndex'] === 'number' ? { batchIndex: rec['batchIndex'] } : {}),
        ...(typeof rec['currentBatchPosition'] === 'number'
          ? { currentBatchPosition: rec['currentBatchPosition'] }
          : {}),
        ...(typeof rec['currentBatchSize'] === 'number'
          ? { currentBatchSize: rec['currentBatchSize'] }
          : {}),
      };
    }
    return { kind: 'unknown', text: JSON.stringify(item) };
  }
  // boxes / batches → StorageEntity with entityId + entityType.
  if (typeof rec['entityId'] === 'string' && typeof rec['entityType'] === 'string') {
    return {
      kind: 'container',
      entityType: rec['entityType'] as ContainerListingEntry['entityType'],
      entityId: rec['entityId'],
      ...(typeof rec['sourceRunId'] === 'string' ? { sourceRunId: rec['sourceRunId'] } : {}),
    };
  }
  return { kind: 'unknown', text: JSON.stringify(item) };
}

/**
 * Order a Storage_Batch's card children ascending by `sku` for the drill-down's
 * optional "Sort by SKU" view. Cards WITH a sku come first, ascending by sku
 * (locale-insensitive code-point compare); cards WITHOUT a sku trail, preserving
 * their original relative order (a stable partition). Non-card children, if any,
 * are returned unchanged in place — this is a pure view transform over whatever
 * is currently loaded, so it's correct once all pages are present (a batch is a
 * single contents page in practice).
 */
export function sortCardsBySku(children: readonly DrillChild[]): DrillChild[] {
  const withSku: Array<{ child: DrillChild; sku: string; i: number }> = [];
  const rest: DrillChild[] = [];
  children.forEach((child, i) => {
    if (child.kind === 'card' && child.sku !== undefined && child.sku !== '') {
      withSku.push({ child, sku: child.sku, i });
    } else {
      rest.push(child);
    }
  });
  withSku.sort((a, b) => (a.sku < b.sku ? -1 : a.sku > b.sku ? 1 : a.i - b.i));
  return [...withSku.map((x) => x.child), ...rest];
}

/**
 * Case-insensitive substring filter for a drill child, used by the entity
 * detail page's Contents search box. An empty/whitespace query matches
 * everything. A container matches on its entityId or sourceRunId; a card matches
 * on its cardId, shortCode, sku, or state; an `unknown` child matches its raw
 * text. Pure — over whatever children are currently loaded.
 */
export function drillChildMatches(child: DrillChild, query: string): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const fields: Array<string | undefined> =
    child.kind === 'container'
      ? [child.entityId, child.sourceRunId]
      : child.kind === 'card'
        ? [child.cardId, child.shortCode, child.sku, child.state]
        : [child.text];
  return fields.some((field) =>
    fieldSearchValues(field).some((value) => terms.some((term) => value.includes(term))),
  );
}

/**
 * Case-insensitive substring filter for a top-level Browse list row. Matches
 * the row's visible identifiers and codes plus derived batch workflow words
 * like "pending", "rejected", "put away". Empty/whitespace
 * queries match everything. Pure — over the loaded listing rows only.
 */
export function containerListingEntryMatches(entry: ContainerListingEntry, query: string): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const status = entry.batchStatus;
  const fields: Array<string | undefined> = [
    entry.entityId,
    entry.entityType,
    entry.rackCode,
    entry.shelfCode,
    entry.rowCode,
    entry.parentId,
    entry.sourceRunId,
    entry.runState,
    status === undefined
      ? undefined
      : [
          'cards',
          String(status.cardCount),
          status.imageApprovalComplete
            ? 'approved image approval complete'
            : 'pending image approval',
          status.imageApproval.pendingCount > 0 ? 'pending' : undefined,
          status.imageApproval.rejectedCount > 0 ? 'rejected' : undefined,
          status.imageApproval.missingCount > 0 ? 'missing' : undefined,
          `${status.imageApproval.approvedCount} approved`,
          `${status.imageApproval.pendingCount} pending`,
          `${status.imageApproval.rejectedCount} rejected`,
          status.putAway ? 'put away putaway stored' : 'not put away not placed',
        ]
          .filter((value): value is string => value !== undefined)
          .join(' '),
  ];
  return fields.some((field) =>
    fieldSearchValues(field).some((value) => terms.some((term) => value.includes(term))),
  );
}

function searchTerms(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const terms = [q];
  const body = entityIdBody(q);
  if (body !== undefined) terms.push(body);
  return [...new Set(terms)];
}

function fieldSearchValues(field: string | undefined): string[] {
  if (field === undefined) return [];
  const value = field.toLowerCase();
  const values = [value];
  const body = entityIdBody(value);
  if (body !== undefined) values.push(body);
  return values;
}

function entityIdBody(value: string): string | undefined {
  const parts = value.split(':');
  return parts.length === 3 && parts[0] === 'tcvs' ? parts[2] : undefined;
}

/** Human label for an `entityType` in breadcrumbs / headings. */
export const ENTITY_TYPE_LABEL: Record<ContainerListingEntry['entityType'], string> = {
  RACK: 'Rack',
  SHELF: 'Shelf',
  BOX: 'Box',
  ROW: 'Row',
  BATCH: 'Storage_Batch',
};

/**
 * One node on the Browse drill-down breadcrumb path: the entity being viewed,
 * plus a Shelf's human `shelfCode` so a restored breadcrumb reads by code rather
 * than ULID.
 */
export interface DrillNode {
  entityType: ContainerListingEntry['entityType'];
  entityId: string;
  rackCode?: string;
  shelfCode?: string;
  rowCode?: string;
}

/**
 * Serialize a drill-down breadcrumb path to a compact, URL-safe string so the
 * operator's place survives navigating to a card detail page and back (the
 * browser Back button restores the URL, hence the path). Encodes the minimal
 * `[type, id, shelfCode?]` tuple per node as JSON. Returns `''` for an empty
 * path so the caller can drop the query param entirely.
 */
export function serializeDrillPath(path: readonly DrillNode[]): string {
  if (path.length === 0) return '';
  return JSON.stringify(
    path.map((n) => [
      n.entityType,
      n.entityId,
      ...(n.shelfCode !== undefined ? [n.shelfCode] : []),
    ]),
  );
}

/**
 * Parse a drill-down path produced by {@link serializeDrillPath}. Defensive:
 * any malformed input (bad JSON, wrong shape, unknown entityType) yields `[]`
 * rather than throwing, so a hand-edited or stale URL degrades to "no drill open"
 * instead of crashing the page.
 */
export function parseDrillPath(raw: string | null | undefined): DrillNode[] {
  if (raw === null || raw === undefined || raw === '') return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: DrillNode[] = [];
  for (const entry of parsed) {
    if (!Array.isArray(entry)) return [];
    const [type, id, shelfCode] = entry as unknown[];
    if (typeof type !== 'string' || !(type in ENTITY_TYPE_LABEL)) return [];
    if (typeof id !== 'string' || id === '') return [];
    out.push({
      entityType: type as DrillNode['entityType'],
      entityId: id,
      ...(typeof shelfCode === 'string' ? { shelfCode } : {}),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Creation date (decoded from the entity id's ULID)
// ---------------------------------------------------------------------------

/**
 * Crockford base32 alphabet used by ULID (excludes I, L, O, U). The ULID spec
 * encodes its 48-bit millisecond timestamp in the first 10 characters.
 */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Decode the creation time of a Storage_Entity from its id, with no backend
 * round-trip. Every Storage_Entity id is `tcvs:<type>:<ULID>` and a ULID's
 * first 10 characters are its creation timestamp (ms since epoch) in Crockford
 * base32 — so the listing's existing `entityId` already carries the creation
 * date. Because boxes/batches list by `entityId` (the ULID) and ULIDs are
 * time-sortable, the listing order is already chronological.
 *
 * Returns `undefined` when the id body is not a parseable ULID timestamp (e.g.
 * a legacy/non-ULID id), so callers can render a blank cell rather than a bogus
 * date. Pure — no DOM, no clock, no network.
 */
export function entityCreatedAt(entityId: string): Date | undefined {
  const body = idBody(entityId).toUpperCase();
  if (body.length < 10) return undefined;
  let ms = 0;
  for (let i = 0; i < 10; i++) {
    const value = CROCKFORD.indexOf(body[i] as string);
    if (value === -1) return undefined; // not a valid Crockford char → not a ULID
    ms = ms * 32 + value;
  }
  // A ULID timestamp is 48 bits; anything past that is out of range.
  if (ms > 0xffffffffffff) return undefined;
  return new Date(ms);
}

/**
 * Format a listed entry's creation date for the browse table. Returns a short
 * locale date-time string, or an em dash when the id carries no decodable
 * timestamp. Pure aside from `toLocaleString` (locale/timezone of the host).
 */
export function formatListingDate(entityId: string): string {
  const date = entityCreatedAt(entityId);
  if (date === undefined || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

/** Sort direction for the browse table's Created column. */
export type SortDirection = 'asc' | 'desc';

/**
 * Return a new array of listing entries ordered by their decoded creation date
 * (Req: Shelves / Boxes / Batches Created-column sort). Pure — never mutates the input.
 *
 * `asc` puts oldest first, `desc` newest first. Entries whose id carries no
 * decodable ULID timestamp (legacy/non-ULID ids) sort to the END in both
 * directions so a missing date never displaces real rows; their relative order
 * is preserved (a stable tiebreak). Equal timestamps also keep their incoming
 * relative order, so the sort is stable.
 */
export function sortEntriesByDate(
  entries: readonly ContainerListingEntry[],
  direction: SortDirection,
): ContainerListingEntry[] {
  const factor = direction === 'asc' ? 1 : -1;
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const at = entityCreatedAt(a.entry.entityId)?.getTime();
      const bt = entityCreatedAt(b.entry.entityId)?.getTime();
      // Undecodable dates sort last regardless of direction.
      if (at === undefined && bt === undefined) return a.index - b.index;
      if (at === undefined) return 1;
      if (bt === undefined) return -1;
      if (at !== bt) return (at - bt) * factor;
      return a.index - b.index; // stable tiebreak
    })
    .map(({ entry }) => entry);
}

// ---------------------------------------------------------------------------
// Label sheet partition
// ---------------------------------------------------------------------------

/** A single resolved label tile for the sheet. */
export interface LabelTile {
  entityId: string;
  token: string;
  parsed: QrToken;
}

/** A per-entity label fetch result, keyed by the entity it was requested for. */
export interface LabelResultRef {
  entityId: string;
  result: ApiResult<LabelResponse>;
}

/**
 * Partition per-entity label results into the sheet's renderable tiles and the
 * entities whose label could not be obtained (Req 8.3, 8.5).
 *
 * Each input result is either a resolved label (→ a tile) or a failure (→ the
 * `failed` list), so tiles and failures partition the input with no overlap and
 * no loss. Input order is preserved in both output lists so the sheet renders
 * tiles in selection order and reports failures predictably.
 */
export function buildLabelSheet(results: readonly LabelResultRef[]): {
  tiles: LabelTile[];
  failed: string[];
} {
  const tiles: LabelTile[] = [];
  const failed: string[] = [];
  for (const { entityId, result } of results) {
    if (result.ok) {
      tiles.push({ entityId, token: result.data.token, parsed: result.data.parsed });
    } else {
      failed.push(entityId);
    }
  }
  return { tiles, failed };
}

// ---------------------------------------------------------------------------
// Selection toggle
// ---------------------------------------------------------------------------

/**
 * Toggle an entity id's membership in a selection set, returning a new set and
 * never mutating the input (Req 8.1).
 *
 * Toggling an id once flips its membership; toggling it twice returns a set
 * with the same membership as the original (a pure involution).
 */
export function toggleSelection(selected: ReadonlySet<string>, entityId: string): Set<string> {
  const next = new Set(selected);
  if (next.has(entityId)) {
    next.delete(entityId);
  } else {
    next.add(entityId);
  }
  return next;
}
